import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface Popup {
  id: string;
  title: string;
  image_url: string;
  link_url: string | null;
}

/**
 * 메인 팝업 — 활성 팝업을 각각 독립된 창으로 동시에 띄운다.
 * - 데스크톱(md 이상): 가로로 나란히. 개수가 많으면 폭을 줄이고, 그래도 넘치면 다음 줄로 넘긴다.
 * - 모바일: 같은 자리에 겹쳐 쌓고 살짝 어긋나게 둬서 여러 장임을 보인다. 위(display_order 앞)부터 하나씩 닫힌다.
 * - 닫기·오늘 하루 안 보기는 창마다 따로 동작한다.
 */
const PopupModal: React.FC = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [hideTodayIds, setHideTodayIds] = useState<string[]>([]);

  useEffect(() => {
    fetchActivePopups();
  }, []);

  const fetchActivePopups = async () => {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('popups')
      .select('id, title, image_url, link_url')
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching popups:', error);
      return;
    }

    if (data && data.length > 0) {
      const hiddenPopups = getHiddenPopups();
      setPopups(data.filter(popup => !hiddenPopups.includes(popup.id)));
    }
  };

  const getHiddenPopups = (): string[] => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem('hiddenPopups');

    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      if (parsed.date === today) {
        return parsed.ids || [];
      }
      return [];
    } catch {
      return [];
    }
  };

  const hidePopupToday = (popupId: string) => {
    const today = new Date().toDateString();
    const hiddenPopups = getHiddenPopups();

    if (!hiddenPopups.includes(popupId)) {
      hiddenPopups.push(popupId);
    }

    localStorage.setItem('hiddenPopups', JSON.stringify({
      date: today,
      ids: hiddenPopups
    }));
  };

  const toggleHideToday = (popupId: string, checked: boolean) => {
    setHideTodayIds(prev => checked ? [...prev, popupId] : prev.filter(id => id !== popupId));
  };

  const handleClose = (popupId: string) => {
    if (hideTodayIds.includes(popupId)) {
      hidePopupToday(popupId);
    }
    setPopups(prev => prev.filter(p => p.id !== popupId));
  };

  const handleImageClick = (popup: Popup) => {
    if (!popup.link_url) return;
    if (popup.link_url.startsWith('http')) {
      window.open(popup.link_url, '_blank');
    } else {
      window.location.href = popup.link_url;
    }
  };

  if (popups.length === 0) {
    return null;
  }

  const count = popups.length;
  // 데스크톱 창 폭: 한 줄에 다 들어가도록 나누되 260~400px 사이로
  const desktopWidth = `max(260px, min(400px, calc((100vw - 4rem - ${count - 1}rem) / ${count})))`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 md:py-10">
        <div className="grid md:flex md:flex-wrap md:justify-center md:items-start gap-4">
          {popups.map((popup, index) => (
            <div
              key={popup.id}
              role="dialog"
              aria-label={popup.title}
              className="[grid-area:1/1] self-start md:[grid-area:auto] translate-x-[calc(var(--i)*10px)] translate-y-[calc(var(--i)*10px)] md:translate-x-0 md:translate-y-0 relative bg-white rounded-lg shadow-2xl overflow-hidden w-[min(420px,calc(100vw-3rem))] md:w-[var(--w)]"
              style={{ zIndex: count - index, '--i': index, '--w': desktopWidth } as React.CSSProperties}
            >
              {/* 닫기 버튼 */}
              <button
                onClick={() => handleClose(popup.id)}
                className="absolute top-3 right-3 z-10 p-1 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors"
                aria-label="닫기"
              >
                <XMarkIcon className="w-6 h-6 text-gray-700" />
              </button>

              {/* 팝업 이미지 */}
              <div
                className={popup.link_url ? 'cursor-pointer' : ''}
                onClick={popup.link_url ? () => handleImageClick(popup) : undefined}
              >
                <img
                  src={popup.image_url}
                  alt={popup.title}
                  decoding="async"
                  className="block w-full h-auto"
                />
              </div>

              {/* 하단 컨트롤 */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideTodayIds.includes(popup.id)}
                    onChange={(e) => toggleHideToday(popup.id, e.target.checked)}
                    className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
                  />
                  <span className="ml-2 text-sm text-gray-600">오늘 하루 안 보기</span>
                </label>

                <button
                  onClick={() => handleClose(popup.id)}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PopupModal;
